import React from "react"
import { FormikProps } from "formik"
import clsx from "clsx"
import { toast } from "react-toastify"
import MySelectCreatableFormField from "@common-components/fields/MySelectCreatableFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyDateMonthYearFormField from "@common-components/fields/MyDateMonthYearFormField"
import { CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM } from "@common-constants/*"
import { CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT } from "../../../../../../../../endpoints"

// MISE À JOUR DE L'INTERFACE ICI
interface Props {
     helpers: FormikProps<any>
     validationSchema: any
     index: number
     namePrefix?: string
     detailsIndemniteKm: any
     setDetailsIndemniteKm: any // Ajout de la propriété manquante
     calculateDistance: (idx: number, dep?: string, arr?: string) => Promise<number>
     syncKM: (idx: number, dist: number, ar: boolean, nb: number) => void
}

export const NdfGroupedMileageSection = ({
     helpers,
     validationSchema,
     index,
     namePrefix = "childs",
     detailsIndemniteKm,
     setDetailsIndemniteKm, // Récupération dans les props
     calculateDistance,
     syncKM,
}: Props) => {
     if (helpers.values[namePrefix][index].categorie !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM) return null

     const item = helpers.values[namePrefix][index]
     const joursKm: number[] = (item.joursKm || []).map(Number)
     const maxTrajets = item.nbTrajets || 0
     const isAgendaReady = item.moisAnneeKm && item.adresseDepart && item.adresseArrive && maxTrajets > 0
     const currentDistDetails = detailsIndemniteKm[index] || { distanceEnKmEntreDepartEtDestination: 0, montantTTC: 0 }

     const renderAgenda = () => {
          if (!item.moisAnneeKm || !maxTrajets) return null
          const [year, month] = item.moisAnneeKm.split("-").map(Number)
          const daysInMonth = new Date(year, month, 0).getDate()
          const monthLabel = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date(year, month - 1))
          const isExactMatch = joursKm.length === maxTrajets

          // Gestion sécurisée des erreurs Formik pour les tableaux
          const errors = helpers.errors[namePrefix] ? (helpers.errors[namePrefix] as any)[index]?.joursKm : undefined

          return (
               <div className="row mb-6">
                    <div className="col-lg-4"></div>
                    <div className="col-lg-8">
                         <div
                              className={clsx(
                                   "p-5 border border-dashed rounded",
                                   errors
                                        ? "border-danger bg-light-danger bg-opacity-25"
                                        : isExactMatch
                                        ? "border-success bg-light-success bg-opacity-25"
                                        : "border-primary bg-light-primary bg-opacity-25"
                              )}
                         >
                              <h6 className={clsx("mb-4 fw-boldest", errors ? "text-danger" : isExactMatch ? "text-success" : "text-primary")}>
                                   Sélectionnez {maxTrajets} jours en {monthLabel}
                              </h6>
                              <div className="d-flex flex-wrap gap-2">
                                   {Array.from({ length: daysInMonth }, (_, i) => {
                                        const d = i + 1
                                        const dt = new Date(year, month - 1, d)
                                        const isWE = dt.getDay() === 0 || dt.getDay() === 6
                                        const letter = new Intl.DateTimeFormat("fr-FR", { weekday: "narrow" }).format(dt)
                                        const isSel = joursKm.includes(d)
                                        return (
                                             <button
                                                  key={d}
                                                  type="button"
                                                  style={{ width: "38px", height: "42px" }}
                                                  onClick={() => {
                                                       if (isSel) {
                                                            helpers.setFieldValue(
                                                                 `${namePrefix}[${index}].joursKm`,
                                                                 joursKm.filter(x => x !== d)
                                                            )
                                                       } else {
                                                            if (joursKm.length >= maxTrajets) {
                                                                 toast.warning(`Vous ne pouvez pas sélectionner plus de ${maxTrajets} jours.`)
                                                                 return
                                                            }
                                                            helpers.setFieldValue(
                                                                 `${namePrefix}[${index}].joursKm`,
                                                                 [...joursKm, d].sort((a, b) => a - b)
                                                            )
                                                       }
                                                  }}
                                                  className={clsx(
                                                       "btn btn-icon d-flex flex-column fw-bold",
                                                       isSel
                                                            ? "btn-primary shadow-sm"
                                                            : isWE
                                                            ? "bg-light text-muted opacity-75 border-gray-300"
                                                            : "bg-white text-gray-700 border-gray-300"
                                                  )}
                                             >
                                                  <span className="fs-9 opacity-75">{letter}</span>
                                                  <span className="fs-6 mt-1">{d}</span>
                                             </button>
                                        )
                                   })}
                              </div>
                              <div className="mt-4 fs-7 d-flex align-items-center">
                                   <i className={clsx("fas fa-info-circle me-2", errors ? "text-danger" : isExactMatch ? "text-success" : "text-muted")}></i>
                                   <span className="text-muted">Jours :</span>
                                   <strong className={clsx("ms-1", isExactMatch ? "text-success" : "text-danger")}>{joursKm.length}</strong>
                                   <span className="text-muted"> / {maxTrajets}</span>
                              </div>
                              {errors && <div className="text-danger mt-2 fs-7 fw-bold">{errors}</div>}
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               <MyDateMonthYearFormField
                    name={`${namePrefix}[${index}].moisAnneeKm`}
                    formikProps={helpers}
                    validationSchema={validationSchema}
                    label={<div className="required">Mois et Année</div>}
                    classNameLabel="col-lg-4"
                    classNameField="col-lg-8"
                    onChange={() => helpers.setFieldValue(`${namePrefix}[${index}].joursKm`, [])}
                    placeholder="MM/YYYY"
                    classNameLayout="mb-2"
               />
               {item.moisAnneeKm && (
                    <div>
                         <MySelectCreatableFormField
                              name={`${namePrefix}[${index}].adresseDepart`}
                              label={<div className="required">Départ</div>}
                              endpoint={CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT}
                              method="GET"
                              isMulti={false}
                              classNameLabel="col-lg-4"
                              classNameField="col-lg-8"
                              formikProps={helpers}
                              validationSchema={validationSchema}
                              onChangeCallback={v => calculateDistance(index, v as string, item.adresseArrive).then(d => syncKM(index, d, !!item.isAllerRetour, item.nbTrajets))}
                              placeholder="Saisissez l'adresse de départ"
                              classNameLayout="mb-2"
                         />
                         <MySelectCreatableFormField
                              name={`${namePrefix}[${index}].adresseArrive`}
                              label={<div className="required">Arrivée</div>}
                              endpoint={CONST_API_UTILS_GET_GOOGLE_MAPS_ADDRESS_AUTOCOMPLETION_ENDPOINT}
                              method="GET"
                              isMulti={false}
                              classNameLabel="col-lg-4"
                              classNameField="col-lg-8"
                              formikProps={helpers}
                              validationSchema={validationSchema}
                              onChangeCallback={v => calculateDistance(index, item.adresseDepart, v as string).then(d => syncKM(index, d, !!item.isAllerRetour, item.nbTrajets))}
                              placeholder="Saisissez l'adresse d'arrivée"
                              classNameLayout="mb-2"
                         />
                         <div className="row mb-2">
                              <label className="col-lg-4 col-form-label fs-6 fw-bolder required">Trajets</label>
                              <div className="col-lg-8 d-flex gap-2">
                                   <MyInputFormField
                                        name={`${namePrefix}[${index}].nbTrajets`}
                                        type="number"
                                        placeholder="Nombre de trajets"
                                        formikProps={helpers}
                                        validationSchema={validationSchema}
                                        classNameLayout="mb-0"
                                        onChangeCallback={v => {
                                             syncKM(index, currentDistDetails.distanceEnKmEntreDepartEtDestination, !!item.isAllerRetour, v)
                                             if (v < joursKm.length) helpers.setFieldValue(`${namePrefix}[${index}].joursKm`, [])
                                        }}
                                   />
                                   <div className="d-flex align-items-center">
                                        <MyCheckboxFormField
                                             name={`${namePrefix}[${index}].isAllerRetour`}
                                             formikProps={helpers}
                                             onChangeCallback={v => syncKM(index, currentDistDetails.distanceEnKmEntreDepartEtDestination, v, item.nbTrajets)}
                                        />
                                        <span className="ms-2">Aller-retour</span>
                                   </div>
                              </div>
                         </div>
                         {isAgendaReady && (
                              <div>
                                   {renderAgenda()}
                                   <MyInputFormField
                                        name={`${namePrefix}[${index}].distanceTotale`}
                                        type="number"
                                        placeholder="Distance totale en km"
                                        label={<div className="required">Distance totale en Km</div>}
                                        formikProps={helpers}
                                        validationSchema={validationSchema}
                                        classNameLabel="col-lg-4"
                                        classNameField="col-lg-8"
                                        classNameLayout="mb-4"
                                        onChangeCallback={val => syncKM(index, val, false, 1)}
                                   />
                                   <div className="p-4 border border-dashed text-gray-600 mb-6 text-center">
                                        <p className="mb-0 fw-boldest fs-5">Montant TTC: {currentDistDetails.montantTTC?.toFixed(2)}€</p>
                                   </div>
                              </div>
                         )}
                    </div>
               )}
          </>
     )
}
